

import { useState, useEffect } from "react";
import { formatCurrency, localToday } from "@/lib/data";
import { CustomerFinancials } from "./types";
import SearchableDropdown from "../../components/SearchableDropdown";
import ImageUpload from "@/components/ImageUpload";
import { Calendar, User } from "lucide-react";
import { mastersApi } from "@/lib/api";

// INR Icon Component
const INRIcon = ({ className }: { className?: string }) => (
  <span className={className}>₹</span>
);

interface Client {
  id: number;
  name: string;
  email?: string;
  balance: number;
}

interface Event {
  id: number;
  name: string;
  client_id?: number;
  start_date: string;
  financials?: {
    customer?: {
      balance: number;
      total: number;
      paid: number;
    };
  };
}

interface PaymentFormProps {
  balanceDue?: number;
  onSubmit: (data: PaymentFormData) => void;
  onCancel: () => void;
  clients?: Client[];
  events?: Event[];
  selectedClientId?: string;
  onClientChange?: (clientId: string) => void;
  isEventSpecific?: boolean; // If true, don't show client/event selectors
  initialData?: Partial<PaymentFormData> & {
    client_id?: number;
    event_id?: number;
  };
}

export interface PaymentFormData {
  amount: number;
  payment_method: string;
  payment_date: string;
  transaction_id: string;
  notes: string;
  client_id?: number;
  event_id?: number;
  proof_file?: File | null;
}

export default function PaymentForm({ 
  balanceDue, 
  onSubmit, 
  onCancel,
  clients = [],
  events = [],
  selectedClientId,
  onClientChange,
  isEventSpecific = false,
  initialData,
}: PaymentFormProps) {
  const [formData, setFormData] = useState({
    client_id: initialData?.client_id ? String(initialData.client_id) : selectedClientId || "",
    event_id: initialData?.event_id ? String(initialData.event_id) : "",
    amount: initialData?.amount ? String(initialData.amount) : "",
    payment_method: initialData?.payment_method || "cash",
    payment_date: initialData?.payment_date || localToday(),
    transaction_id: initialData?.transaction_id || "",
    notes: initialData?.notes || "",
    proof_file: initialData?.proof_file || null as File | null,
  });

  const [paymentMethodOptions, setPaymentMethodOptions] = useState<{ value: string; label: string }[]>([
    { value: "cash", label: "Cash" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "upi", label: "UPI" },
    { value: "card", label: "Card" },
  ]);

  useEffect(() => {
    mastersApi.paymentMethods.list({ status: 'active' })
      .then((res) => {
        const data = res.data?.data || res.data || [];
        if (data.length > 0) {
          setPaymentMethodOptions(data.map((m: any) => ({ value: m.name, label: m.name })));
        }
      })
      .catch(() => {});
  }, []);

  // Auto-fill amount with balance due when modal opens (for event-specific payments)
  useEffect(() => {
    if (isEventSpecific && balanceDue !== undefined && balanceDue > 0 && !formData.amount) {
      setFormData(prev => ({
        ...prev,
        amount: balanceDue.toString()
      }));
    }
  }, [isEventSpecific, balanceDue]);

  // Format balance without compact notation (e.g., ₹210,000 instead of ₹2.1L)
  const formatFullCurrency = (amount: number): string => {
    if (amount === 0) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Filter events for selected client
  const clientEvents = events.filter(e => 
    formData.client_id && String(e.client_id) === formData.client_id
  );

  const selectedEvent = events.find(e => String(e.id) === formData.event_id);
  const eventBalance = selectedEvent?.financials?.customer?.balance || balanceDue || 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate event_id is selected for non-event-specific forms
    if (!isEventSpecific && !formData.event_id) {
      alert('Please select an event for the payment');
      return;
    }
    
    const paymentData: PaymentFormData = {
      amount: parseFloat(formData.amount),
      payment_method: formData.payment_method,
      payment_date: formData.payment_date,
      transaction_id: formData.transaction_id,
      notes: formData.notes,
      proof_file: formData.proof_file,
    };
    
    // Add client_id and event_id if not event-specific
    if (!isEventSpecific) {
      if (formData.client_id) {
        paymentData.client_id = parseInt(formData.client_id);
      }
      if (formData.event_id) {
        paymentData.event_id = parseInt(formData.event_id);
      }
    }
    
    onSubmit(paymentData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Show balance info only for event-specific payments */}
      {isEventSpecific && balanceDue !== undefined && (
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
          <div className="text-xs text-stone-500">Balance Due</div>
          <div className="text-lg font-bold text-amber-700">{formatFullCurrency(balanceDue)}</div>
        </div>
      )}

      {/* Client Selection - only show if not event-specific */}
      {!isEventSpecific && clients.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Client *</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <SearchableDropdown
              options={[
                { value: "", label: "Select a client" },
                ...clients.map((client) => ({
                  value: String(client.id),
                  label: `${client.name} (Balance: ${formatCurrency(client.balance)})`,
                })),
              ]}
              value={formData.client_id}
              onChange={(value) => {
                setFormData({ ...formData, client_id: value, event_id: "" });
                onClientChange?.(value);
              }}
              placeholder="Select a client"
            />
          </div>
        </div>
      )}

      {/* Event Selection - REQUIRED, show only if client selected and not event-specific */}
      {!isEventSpecific && formData.client_id && (
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">
            Select Event * 
            <span className="text-amber-600 ml-1">(Required)</span>
          </label>
          {clientEvents.length > 0 ? (
            <SearchableDropdown
              options={[
                { value: "", label: "Select an event" },
                ...clientEvents.map((event) => ({
                  value: String(event.id),
                  label: `${event.name} - ${event.start_date} (Balance: ${formatCurrency(event.financials?.customer?.balance || 0)})`,
                })),
              ]}
              value={formData.event_id}
              onChange={(value) => setFormData({ ...formData, event_id: value })}
              placeholder="Select an event"
            />
          ) : (
            <div className="px-3 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-500">
              No events found for this client
            </div>
          )}
          {selectedEvent && eventBalance > 0 && (
            <p className="text-xs text-amber-600 mt-1">
              Current balance: {formatCurrency(eventBalance)}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Amount *</label>
          <div className="flex items-center">
            <span className="px-3 py-2.5 rounded-l-xl border border-r-0 border-stone-200 bg-stone-50 text-stone-600 font-bold text-sm">₹</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={isEventSpecific ? balanceDue : undefined}
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              className="w-full px-3 py-2.5 rounded-r-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              required
            />
          </div>
          {isEventSpecific && balanceDue !== undefined && (
            <p className="text-xs text-stone-400 mt-1">
              Max: {formatFullCurrency(balanceDue)}
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Payment Date *</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              required
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Payment Method *</label>
        <select
          value={formData.payment_method}
          onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          required
        >
          {paymentMethodOptions.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Transaction ID / Reference</label>
        <input
          type="text"
          value={formData.transaction_id}
          onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
          placeholder="Transaction reference number"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes..."
          rows={2}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>

      {/* Proof Upload */}
      <ImageUpload
        selectedFile={formData.proof_file}
        onFileSelect={(file) => setFormData({ ...formData, proof_file: file })}
        maxSize={2 * 1024 * 1024}
        compressToWebP={true}
      />

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update" : "Record"} Payment
        </button>
      </div>
    </form>
  );
}
